import React, { useEffect, useState } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { TextArea, SelectField } from './AdminFields';

const STATUS = [['new', 'New'], ['contacted', 'Contacted'], ['scheduled', 'Scheduled'], ['completed', 'Completed'], ['closed', 'Closed']];
const FORMAT = { onsite: 'On-site', remote: 'Remote', either: 'Either' };

const Row = ({ r, onSaved }) => {
    const [open, setOpen] = useState(false);
    const [f, setF] = useState({ status: r.status, admin_notes: r.admin_notes || '' });
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/admin/training-requests/${r.id}`, f);
            toast.success(`${r.request_number} saved`);
            onSaved();
        } catch {
            toast.error('Could not save — please try again');
        }
        setBusy(false);
    };
    return (
        <div className="border-b border-white/10" data-testid={`admin-training-${r.id}`}>
            <button onClick={() => setOpen(!open)} className="w-full p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-center text-left hover:bg-white/5" data-testid={`admin-training-toggle-${r.id}`}>
                <span className="font-display font-bold text-white">{r.request_number}</span>
                <span className="text-sm text-white/70 truncate">{r.name}</span>
                <span className="text-sm text-white/70 truncate md:col-span-2">{r.people} people · {FORMAT[r.format]} · {r.learn}</span>
                <span className="text-sm text-white/50">{new Date(r.created_at).toLocaleDateString()}</span>
                <span className="label-caps text-xs text-white/60" data-testid={`admin-training-status-label-${r.id}`}>{STATUS.find((s) => s[0] === r.status)?.[1]}</span>
            </button>
            {open && (
                <div className="p-5 bg-brand-bg grid grid-cols-1 md:grid-cols-2 gap-6" data-testid={`admin-training-details-${r.id}`}>
                    <div className="text-sm text-white/70 space-y-1">
                        <div>{r.name}{r.company ? ` · ${r.company}` : ''}</div>
                        <div><a href={`mailto:${r.email}`} className="text-brand-primary">{r.email}</a> · {r.phone}</div>
                        <div>Format: {FORMAT[r.format]} · {r.people} people</div>
                        {r.preferred_dates && <div>Dates: {r.preferred_dates}</div>}
                        {r.location && <div>Location: {r.location}</div>}
                        {r.machine && <div>Machine: {r.machine}</div>}
                        {r.course_names?.length > 0 && <div>Interested in: {r.course_names.join(', ')}</div>}
                        <p className="pt-2 whitespace-pre-line text-white/80">{r.learn}</p>
                    </div>
                    <div className="space-y-3">
                        <SelectField label="Status" value={f.status} onChange={(v) => setF({ ...f, status: v })} options={STATUS} testid={`admin-training-status-${r.id}`} />
                        <TextArea label="Internal notes (price quoted, plan…)" rows={3} value={f.admin_notes} onChange={(v) => setF({ ...f, admin_notes: v })} testid={`admin-training-notes-${r.id}`} />
                        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-brand-ink font-bold px-4 py-2.5 label-caps" data-testid={`admin-training-save-${r.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
                    </div>
                </div>
            )}
        </div>
    );
};

const TrainingRequestsAdmin = () => {
    const [items, setItems] = useState([]);
    const load = () => api.get('/admin/training-requests').then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    return (
        <div data-testid="admin-training-requests">
            <h2 className="font-display font-black uppercase text-3xl mb-2">Training requests</h2>
            <p className="text-white/50 text-sm mb-6">Requests from the Training page. Programs and intro text are editable in the Pages tab.</p>
            <div className="border border-white/10" data-testid="admin-training-list">
                {items.length === 0 && <div className="p-6 text-white/50">No training requests yet.</div>}
                {items.map((r) => <Row key={r.id} r={r} onSaved={load} />)}
            </div>
        </div>
    );
};

export default TrainingRequestsAdmin;
